import { useMutation } from '@apollo/client';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, message } from 'antd';
import React, { useContext, useMemo, useState } from 'react';
import {
  AddButton,
  DeleteButton,
  DragIcon,
  EditButton,
} from '../../../../../../../assets/svg';
import {
  ALLOWED_ACTION_KEYS,
  ALLOWED_ACTION_TYPE,
} from '../../../../../../../common/constants';
import useRouter from '../../../../../../../common/useRouter';
import CanPerform from '../../../../../../../components/CanPerform';
import CommonTable from '../../../../../../../components/CommonTable';
import CommonTooltip from '../../../../../../../components/CommonTooltip';
import EllipsisText from '../../../../../../../components/EllipsisText';
import Portal from '../../../../../../../components/Portal';
import { UPDATE_REGISTER_DETAIL_SEQUENCES } from '../../../../../graphql/Mutation';
import AddEditPropertyModal from '../AddEditPropertyModal';
import FieldIcon from '../FieldIcon';
import DeletePropertyModal from './DeletePropertyModal';

const RowContext = React.createContext({});

const DragHandle = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <DragIcon
      className="cursor-move"
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

const Rows = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props?.['data-row-key'],
  });
  const style = {
    ...props?.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging
      ? {
          position: 'relative',
          zIndex: 9999,
        }
      : {}),
  };
  const contextValue = useMemo(
    () => ({
      setActivatorNodeRef,
      listeners,
    }),
    [setActivatorNodeRef, listeners],
  );
  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

const RegisterDetailsTable = ({
  isEditable,
  propertyDetails,
  setPropertyDetails,
  setTableColumns,
  isAuditor,
  handleRegisterRefetch,
}) => {
  const {
    params: { registerId },
  } = useRouter();
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState();

  const mouseSensor = useSensor(MouseSensor);
  const touchSensor = useSensor(TouchSensor);
  const keyboardSensor = useSensor(KeyboardSensor);
  const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor);

  const [updateRegisterDetailSequence] = useMutation(
    UPDATE_REGISTER_DETAIL_SEQUENCES,
    {
      fetchPolicy: 'network-only',
      onError() {},
      onCompleted() {
        handleRegisterRefetch();
      },
    },
  );

  const handleEditProperty = (property) => {
    setSelectedProperty(property);
    setShowAddPropertyModal(true);
  };

  const handleDeleteProperty = (property) => {
    if (propertyDetails?.length === 1) {
      message.destroy();
      message.error('There must be at-least 1 form field!');
    } else {
      setSelectedProperty(property);
      setShowDeleteModal(true);
    }
  };

  const handleAddProperty = () => {
    setSelectedProperty();
    setShowAddPropertyModal(true);
  };

  const columns = [
    {
      width: isEditable ? '10%' : 0,
      align: 'center',
      className: 'drag-visible',
      render: () => {
        return (
          isEditable && (
            <CommonTooltip
              getPopupContainer={() =>
                // eslint-disable-next-line no-undef
                document.querySelector('.ant-table-content')
              }
              title="Change order"
            >
              <div>
                <DragHandle />
              </div>
            </CommonTooltip>
          )
        );
      },
    },
    {
      title: 'Name',
      dataIndex: 'name',
      className: 'drag-visible',
      key: 'name',
      width: '70%',
      render: (text, record) => {
        return (
          <div className="d-flex">
            {record?.isRequired && <span className="danger-text mr-5">*</span>}
            <FieldIcon fieldType={record?.type} />
            <EllipsisText text={text} />
          </div>
        );
      },
    },
    {
      key: 'action',
      render: (record) => {
        return (
          <div className="d-flex action-icons">
            {isEditable ? (
              <>
                <CommonTooltip trigger="hover" title="Edit">
                  <Button
                    shape="round"
                    icon={<EditButton />}
                    className="edit-button pointer b-0"
                    onClick={() => handleEditProperty(record)}
                  />
                </CommonTooltip>
                <CommonTooltip trigger="hover" title="Delete">
                  <Button
                    className="delete-button pointer b-0"
                    shape="round"
                    icon={<DeleteButton />}
                    disabled={!isEditable}
                    onClick={() => handleDeleteProperty(record)}
                  />
                </CommonTooltip>
              </>
            ) : (
              <div
                className="pointer text-underline mr-10"
                onClick={() => handleEditProperty(record)}
              >
                View
              </div>
            )}
          </div>
        );
      },
    },
  ];

  const DraggableBodyRow = async ({ over, active }) => {
    if (!active || !over || active?.id === over?.id) return;
    const overIndex = over?.data?.current?.sortable?.index;
    const activeIndex = active?.data?.current?.sortable?.index;
    const data = [...propertyDetails];
    setPropertyDetails(arrayMove(data, activeIndex, overIndex));
    try {
      const { errors } = await updateRegisterDetailSequence({
        variables: {
          data: {
            registerId,
            registerDetailId: active?.id,
            sequence: over?.data?.current?.sortable?.index + 1,
          },
        },
      });
      if (errors) {
        throw errors;
      }
    } catch (error) {
      setPropertyDetails(data);
    }
  };

  return (
    <div>
      <Portal portalId="register-details-btn">
        <Portal portalId="add-btn">
          <CanPerform
            action={ALLOWED_ACTION_KEYS.ADD_PROJECT_REGISTER}
            type={ALLOWED_ACTION_TYPE.BOTH}
          >
            {isEditable && (
              <Button
                className="add-details-button b-0"
                shape="round"
                type="primary"
                id="add-btn"
                icon={<AddButton />}
                onClick={handleAddProperty}
              >
                Add
              </Button>
            )}
          </CanPerform>
        </Portal>
      </Portal>
      <DndContext
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={DraggableBodyRow}
        sensors={sensors}
      >
        <SortableContext
          items={propertyDetails?.map((i) => i.id)}
          strategy={verticalListSortingStrategy}
        >
          <CommonTable
            components={{ body: { row: Rows } }}
            columns={columns}
            dataSource={propertyDetails || []}
            className="register-details-table"
            rowKey="id"
            scroll={{
              y: 300,
            }}
          />
        </SortableContext>
      </DndContext>
      {showAddPropertyModal && (
        <AddEditPropertyModal
          showModal={showAddPropertyModal}
          setShowModal={setShowAddPropertyModal}
          selectedProperty={selectedProperty}
          setSelectedProperty={setSelectedProperty}
          propertyDetails={propertyDetails}
          setPropertyDetails={setPropertyDetails}
          setTableColumns={setTableColumns}
          isDisabled={isAuditor || !isEditable}
        />
      )}
      {showDeleteModal && (
        <DeletePropertyModal
          showModal={showDeleteModal}
          setShowModal={setShowDeleteModal}
          selectedProperty={selectedProperty}
          setSelectedProperty={setSelectedProperty}
          setPropertyDetails={setPropertyDetails}
          setTableColumns={setTableColumns}
        />
      )}
    </div>
  );
};

export default RegisterDetailsTable;
